import { Wrench } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { WhatsAppIcon } from "@/shared/components/notifications/WhatsAppIcon";
import { SUPPORT_WHATSAPP_DISPLAY, supportWhatsAppUrl } from "@/shared/constants/support";
import { useCountryLabel } from "@/shared/hooks/useCountryLabel";
import useTranslations from "@/shared/lang/useTranslations";

const COPY = {
  send: { title: "send_title", text: "send_text", message: "send_whatsapp_message" },
  receive: { title: "receive_title", text: "receive_text", message: "receive_whatsapp_message" },
  convert: { title: "convert_title", text: "convert_text", message: "convert_whatsapp_message" },
  buy: { title: "buy_title", text: "buy_text", message: "buy_whatsapp_message" },
  sell: { title: "sell_title", text: "sell_text", message: "sell_whatsapp_message" },
  card_top_up: {
    title: "card_top_up_title",
    text: "card_top_up_text",
    message: "card_top_up_whatsapp_message",
  },
} as const;

export type RailMaintenanceNoticeProps = { className?: string } & (
  | { kind: "send" | "receive"; /** ISO3 of the country whose rails are off. */ country: string }
  | { kind: "convert"; currency: string }
  | { kind: "buy" | "sell"; asset: string }
  | { kind: "card_top_up" }
);

/**
 * What a member reads where Coloca switched a corridor off: that it is in maintenance and coming back, and
 * how to get an urgent operation done meanwhile — a WhatsApp chat with support, opened with a message that
 * says what they need and nothing about who they are.
 */
export function RailMaintenanceNotice(props: RailMaintenanceNoticeProps) {
  const { t } = useTranslations("railMaintenance");
  const countryLabel = useCountryLabel();
  const copy = COPY[props.kind];
  const values = (() => {
    switch (props.kind) {
      case "send":
      case "receive":
        return { country: countryLabel(props.country, props.country) };
      case "convert":
        return { currency: props.currency, phone: SUPPORT_WHATSAPP_DISPLAY };
      case "buy":
      case "sell":
        return { asset: props.asset };
      case "card_top_up":
        return {};
    }
  })();

  return (
    <ColocaAlert
      variant="warning"
      icon={Wrench}
      heading={t(copy.title, values)}
      text={
        // The convert copy already names the number.
        props.kind === "convert" ? (
          t(copy.text, values)
        ) : (
          <>
            {t(copy.text, values)}
            <span className="mt-1 block font-medium">
              {t("help_line", { phone: SUPPORT_WHATSAPP_DISPLAY })}
            </span>
          </>
        )
      }
      className={props.className}
      actions={
        <ColocaButton
          size="sm"
          nativeButton={false}
          render={
            <a href={supportWhatsAppUrl(t(copy.message, values))} target="_blank" rel="noopener noreferrer" />
          }
        >
          <WhatsAppIcon className="text-[#25D366]" />
          {t("whatsapp_cta")}
        </ColocaButton>
      }
    />
  );
}
